package com.rosario.juridico

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ListAlt
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.navigation.NavController
import kotlinx.datetime.*
import com.rosario.juridico.domain.*
import com.rosario.juridico.ui.screens.CalculatorActionLock
import com.rosario.juridico.ui.screens.UsageCounterIndicator
import com.rosario.juridico.ui.components.JuridicoDatePickerDialogKMP
import com.rosario.juridico.ui.screens.toTexto
import com.rosario.juridico.ui.screens.toPesos
import com.rosario.juridico.ui.screens.toMesAnio
import com.rosario.juridico.ui.screens.ThousandSeparatorVisualTransformation
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

// =============================================================================
// MOTOR DE CÁLCULO — Liquidador
// =============================================================================

fun liquidarDeuda(
    items: List<ItemDeuda>,
    tipoTasa: TipoTasa,
    capitalizacion: TipoCapitalizacion,
    fechaCorte: LocalDate,
    numeroExpediente: String,
    actorDemandado: String
): ResultadoLiquidadorDeuda? {
    if (items.isEmpty()) return null

    val resultadosItems = mutableListOf<ResultadoItemDeuda>()
    var capitalTotal = 0.0
    var interesesTotal = 0.0

    for (item in items) {
        if (item.monto <= 0) continue
        
        val fechaFin = if (fechaCorte > item.fechaDevengamiento) fechaCorte
                       else item.fechaDevengamiento

        val calc = calcularIntereses(
            capital       = item.monto,
            fechaInicio   = item.fechaDevengamiento,
            fechaFin      = fechaFin,
            tipoTasa      = tipoTasa,
            capitalizacion = capitalizacion
        ) ?: continue

        val resultadoItem = ResultadoItemDeuda(
            item                 = item,
            intereses            = calc.interesTotal,
            totalItem            = calc.totalAdeudado,
            diasTranscurridos    = calc.diasTotal,
            tasaEfectivaAplicada = calc.tasaEfectiva,
            periodos             = calc.periodos
        )
        resultadosItems.add(resultadoItem)
        capitalTotal   += item.monto
        interesesTotal += calc.interesTotal
    }

    if (resultadosItems.isEmpty()) return null

    return ResultadoLiquidadorDeuda(
        items           = resultadosItems,
        capitalTotal    = capitalTotal,
        interesesTotal  = interesesTotal,
        totalGeneral    = capitalTotal + interesesTotal,
        tipoTasa        = tipoTasa,
        capitalizacion  = capitalizacion,
        fechaCorte      = fechaCorte,
        numeroExpediente = numeroExpediente,
        actorDemandado  = actorDemandado
    )
}

// =============================================================================
// GENERADOR DE TEXTO JUDICIAL
// =============================================================================

object GeneradorTextoJudicial {
    fun generar(resultado: ResultadoLiquidadorDeuda): String {
        val sb = StringBuilder()
        val hoy = resultado.fechaCorte.toTexto()

        sb.appendLine("═".repeat(50))
        sb.appendLine("       LIQUIDACIÓN DE DEUDA CON INTERESES")
        sb.appendLine("═".repeat(50))
        sb.appendLine()

        if (resultado.numeroExpediente.isNotBlank()) sb.appendLine("Expediente N°: ${resultado.numeroExpediente}")
        if (resultado.actorDemandado.isNotBlank()) sb.appendLine("Autos: ${resultado.actorDemandado}")
        sb.appendLine("Fecha de corte: $hoy")
        sb.appendLine("Tasa: ${resultado.tipoTasa.etiqueta}")
        sb.appendLine("Capitalización: ${resultado.capitalizacion.etiqueta}")
        sb.appendLine()

        sb.appendLine("─".repeat(50))
        sb.appendLine("DETALLE:")
        sb.appendLine("─".repeat(50))

        resultado.items.forEachIndexed { idx, r ->
            sb.appendLine("${idx + 1}. ${r.item.descripcion.ifBlank { "Concepto" }} (${r.item.fechaDevengamiento.toMesAnio()})")
            sb.appendLine("   Cap: ${r.item.monto.toPesos()} | Int: ${r.intereses.toPesos()} | Sub: ${r.totalItem.toPesos()}")
        }

        sb.appendLine()
        sb.appendLine("─".repeat(50))
        sb.appendLine("TOTAL GENERAL: ${resultado.totalGeneral.toPesos()}")
        sb.appendLine("─".repeat(50))
        sb.appendLine("\nGenerado por Jurídico Rosario®")

        return sb.toString()
    }
}

// =============================================================================
// PANTALLA PRINCIPAL
// =============================================================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaLiquidadorDeuda(navController: NavController) {
    val coroutineScope = rememberCoroutineScope()

    val hoyKmp = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

    var numeroExpediente by remember { mutableStateOf("") }
    var actorDemandado   by remember { mutableStateOf("") }
    var items by remember { mutableStateOf(listOf(ItemDeuda())) }
    var tipoTasaSel       by remember { mutableStateOf(TipoTasa.BNA_ACTIVA) }
    var capitalizacionSel by remember { mutableStateOf(TipoCapitalizacion.SIMPLE) }
    var fechaCorte        by remember { mutableStateOf(hoyKmp) }
    var tipoActualizacion by remember { mutableStateOf("TASA") } // TASA o IPC

    var expandedTasa          by remember { mutableStateOf(false) }
    var showFechaCorte        by remember { mutableStateOf(false) }
    var showItemPicker        by remember { mutableStateOf<String?>(null) }

    var resultado     by remember { mutableStateOf<ResultadoLiquidadorDeuda?>(null) }
    var resultVisible by remember { mutableStateOf(false) }
    var errorMsg      by remember { mutableStateOf<String?>(null) }
    var showTextoJud  by remember { mutableStateOf(false) }
    var textoJudicial by remember { mutableStateOf("") }

    val clipboardManager = LocalClipboardManager.current
    val scrollState = rememberScrollState()
    val thousandSeparator = ThousandSeparatorVisualTransformation()

    // --- EFECTO DE RESULTADO CON DELAY (FASE 4.1 QA) ---
    LaunchedEffect(resultado) {
        if (resultado != null) {
            delay(400)
            resultVisible = true
            delay(3000)
            scrollState.animateScrollTo(scrollState.maxValue)
        } else {
            resultVisible = false
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .imePadding()
            .verticalScroll(scrollState)
    ) {
        // HEADER
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(
                    brush = Brush.verticalGradient(
                        colors = listOf(Color(0xFF1A4B8C), Color(0xFF3B6DAE))
                    ),
                    shape = RoundedCornerShape(bottomStart = 32.dp, bottomEnd = 32.dp)
                )
                .padding(horizontal = 24.dp)
                .padding(top = 20.dp, bottom = 40.dp)
        ) {
            Column {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = { navController.popBackStack() }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Volver", tint = Color.White)
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text("Liquidador de Deuda", color = Color.White, fontSize = 22.sp, fontWeight = FontWeight.ExtraBold)
                        Text("Liquidación completa y texto judicial", color = Color.White.copy(alpha = 0.7f), fontSize = 12.sp)
                    }
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                UsageCounterIndicator()
            }
        }

        // FORMULARIO PRINCIPAL
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .offset(y = (-20).dp),
            shape = RoundedCornerShape(20.dp),
            elevation = CardDefaults.cardElevation(6.dp)
        ) {
            Column(modifier = Modifier.padding(20.dp)) {
                // Datos del Expediente
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Gavel, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Datos del Expediente", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                }
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = numeroExpediente, 
                    onValueChange = { numeroExpediente = it }, 
                    label = { Text("N° de Expediente") }, 
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = actorDemandado, 
                    onValueChange = { actorDemandado = it }, 
                    label = { Text("Carátula / Autos") }, 
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )

                HorizontalDivider(modifier = Modifier.padding(vertical = 20.dp))

                // Conceptos
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.AutoMirrored.Filled.ListAlt, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Conceptos de Deuda", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                }
                Spacer(modifier = Modifier.height(12.dp))
                
                items.forEachIndexed { index, item ->
                    ItemDeudaCard(
                        index = index,
                        item = item,
                        thousandSeparator = thousandSeparator,
                        onUpdate = { nuevo ->
                            items = items.toMutableList().also { it[index] = nuevo }
                            resultado = null
                        },
                        onDelete = {
                            if (items.size > 1) {
                                items = items.toMutableList().also { it.removeAt(index) }
                                resultado = null
                            }
                        },
                        onPickFecha = { showItemPicker = item.id }
                    )
                }

                TextButton(
                    onClick = { items = items + ItemDeuda(); resultado = null }, 
                    modifier = Modifier.align(Alignment.CenterHorizontally).padding(top = 8.dp)
                ) {
                    Icon(Icons.Default.AddCircleOutline, null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Agregar Período")
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 20.dp))

                // Opciones de Cálculo
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Tune, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Opciones de Cálculo", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                }
                Spacer(modifier = Modifier.height(16.dp))

                Text("Tasa aplicable", fontWeight = FontWeight.Bold, fontSize = 14.sp, modifier = Modifier.padding(bottom = 8.dp))
                ExposedDropdownMenuBox(expanded = expandedTasa, onExpandedChange = { expandedTasa = it }) {
                    OutlinedTextField(
                        value = tipoTasaSel.etiqueta, 
                        onValueChange = {}, 
                        readOnly = true, 
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedTasa) }, 
                        modifier = Modifier.fillMaxWidth().menuAnchor(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expandedTasa, onDismissRequest = { expandedTasa = false }) {
                        TipoTasa.entries.forEach { tasa -> 
                            DropdownMenuItem(
                                text = { Text(tasa.etiqueta) }, 
                                onClick = { 
                                    tipoTasaSel = tasa
                                    expandedTasa = false
                                    resultado = null 
                                }
                            ) 
                        }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
                Text("Interés", fontWeight = FontWeight.Bold, fontSize = 14.sp, modifier = Modifier.padding(bottom = 8.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    ActualizacionChip(
                        text = "Simple", 
                        selected = capitalizacionSel == TipoCapitalizacion.SIMPLE, 
                        onClick = { capitalizacionSel = TipoCapitalizacion.SIMPLE; resultado = null }
                    )
                    ActualizacionChip(
                        text = "Compuesto", 
                        selected = capitalizacionSel == TipoCapitalizacion.COMPUESTO, 
                        onClick = { capitalizacionSel = TipoCapitalizacion.COMPUESTO; resultado = null }
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))
                Text("Calcular hasta (fecha de corte)", fontWeight = FontWeight.Bold, fontSize = 14.sp, modifier = Modifier.padding(bottom = 8.dp))
                Box(modifier = Modifier.fillMaxWidth().clickable { showFechaCorte = true }) {
                    OutlinedTextField(
                        value = fechaCorte.toTexto(),
                        onValueChange = {},
                        readOnly = true,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = false,
                        trailingIcon = { Icon(Icons.Default.CalendarToday, null, modifier = Modifier.size(20.dp)) },
                        colors = OutlinedTextFieldDefaults.colors(
                            disabledTextColor = MaterialTheme.colorScheme.onSurface,
                            disabledBorderColor = MaterialTheme.colorScheme.outline,
                            disabledTrailingIconColor = MaterialTheme.colorScheme.primary
                        ),
                        shape = RoundedCornerShape(12.dp)
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Botón Calcular
                CalculatorActionLock(
                    navController = navController,
                    onAction = {
                        errorMsg = null
                        val validos = items.filter { it.monto > 0 }
                        if (validos.isEmpty()) { 
                            errorMsg = "Ingresá montos válidos."
                            return@CalculatorActionLock
                        }
                        resultado = liquidarDeuda(validos, tipoTasaSel, capitalizacionSel, fechaCorte, numeroExpediente, actorDemandado)
                    }
                ) { onClick ->
                    Button(
                        onClick = onClick,
                        modifier = Modifier.fillMaxWidth().height(56.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFFD700)),
                        shape = RoundedCornerShape(16.dp)
                    ) {
                        Text("Calcular Liquidación", color = Color.Black, fontWeight = FontWeight.ExtraBold, fontSize = 16.sp)
                    }
                }

                if (errorMsg != null) {
                    Text(text = errorMsg!!, color = MaterialTheme.colorScheme.error, modifier = Modifier.padding(top = 8.dp))
                }
            }
        }

        // RESULTADOS
        AnimatedVisibility(visible = resultVisible) {
            resultado?.let { res ->
                val textoJudicialCompleto = GeneradorTextoJudicial.generar(res)
                val textoCopia = """
                    RESUMEN LIQUIDACIÓN DE DEUDA
                    Expediente: ${res.numeroExpediente.ifBlank { "-" }}
                    Autos: ${res.actorDemandado.ifBlank { "-" }}
                    Fecha de Corte: ${res.fechaCorte.toTexto()}
                    -------------------------------------------
                    TOTAL GENERAL: ${res.totalGeneral.toPesos()}
                    Capital: ${res.capitalTotal.toPesos()}
                    Intereses: ${res.interesesTotal.toPesos()}
                    -------------------------------------------
                    Calculado con www.juridicorosario.com
                """.trimIndent()

                Column(modifier = Modifier.padding(horizontal = 16.dp)) {
                    CajaResultadoAccionable(
                        titulo = "Resultado de la Liquidación",
                        textoParaCompartir = textoCopia,
                        colorFondo = MaterialTheme.colorScheme.primaryContainer
                    ) {
                        Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                            Text("TOTAL GENERAL", fontSize = 12.sp, fontWeight = FontWeight.Bold)
                            Text(res.totalGeneral.toPesos(), fontSize = 32.sp, fontWeight = FontWeight.ExtraBold, color = MaterialTheme.colorScheme.primary)

                            HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp))

                            res.items.forEach { r ->
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                    Text(r.item.descripcion.ifBlank { "Concepto" }, fontSize = 13.sp)
                                    Text(r.totalItem.toPesos(), fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                }
                                Spacer(modifier = Modifier.height(4.dp))
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        CalculatorActionLock(
                            navController = navController,
                            onAction = {
                                textoJudicial = textoJudicialCompleto
                                showTextoJud = true
                            }
                        ) { onClick ->
                            Button(
                                onClick = onClick,
                                modifier = Modifier.weight(1f).height(48.dp),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Icon(Icons.Default.Article, null, modifier = Modifier.size(18.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Ver Escrito", fontSize = 14.sp)
                            }
                        }
                    }
                }
            }
        }
        Spacer(modifier = Modifier.height(40.dp))
    }

    // DIALOGS
    if (showTextoJud) {
        Dialog(onDismissRequest = { showTextoJud = false }) {
            Card(modifier = Modifier.fillMaxWidth().fillMaxHeight(0.8f).padding(16.dp), shape = RoundedCornerShape(20.dp)) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text("Previsualización Escrito Judicial", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                    Spacer(modifier = Modifier.height(12.dp))
                    Box(modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()).background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(12.dp)).padding(12.dp)) {
                        Text(textoJudicial, fontFamily = FontFamily.Monospace, fontSize = 12.sp)
                    }
                    Spacer(modifier = Modifier.height(16.dp))
                    Button(
                        onClick = { 
                            clipboardManager?.setText(AnnotatedString(textoJudicial))
                            showTextoJud = false 
                        }, 
                        modifier = Modifier.fillMaxWidth().height(48.dp),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text("Copiar y Cerrar")
                    }
                }
            }
        }
    }

    if (showFechaCorte) { 
        JuridicoDatePickerDialogKMP(onDateSelected = { fechaCorte = it; showFechaCorte = false; resultado = null }, onDismiss = { showFechaCorte = false }) 
    }
    
    if (showItemPicker != null) {
        JuridicoDatePickerDialogKMP(onDateSelected = { kmpDate -> 
            items = items.map { if (it.id == showItemPicker) it.copy(fechaDevengamiento = kmpDate) else it }
            showItemPicker = null
            resultado = null 
        }, onDismiss = { showItemPicker = null })
    }
}

@Composable
private fun ItemDeudaCard(
    index: Int,
    item: ItemDeuda,
    thousandSeparator: ThousandSeparatorVisualTransformation,
    onUpdate: (ItemDeuda) -> Unit,
    onDelete: () -> Unit,
    onPickFecha: () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.4f)),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(modifier = Modifier.size(24.dp).background(MaterialTheme.colorScheme.primary.copy(0.1f), CircleShape), contentAlignment = Alignment.Center) {
                    Text("${index + 1}", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text("Concepto", fontWeight = FontWeight.Bold, fontSize = 14.sp)
                Spacer(modifier = Modifier.weight(1f))
                IconButton(onClick = onDelete, modifier = Modifier.size(24.dp)) {
                    Icon(Icons.Default.Delete, null, tint = MaterialTheme.colorScheme.error.copy(0.7f), modifier = Modifier.size(18.dp))
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(
                value = item.descripcion, 
                onValueChange = { onUpdate(item.copy(descripcion = it)) }, 
                placeholder = { Text("Descripción (ej. Cuota 1)") }, 
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(8.dp)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = if (item.monto == 0.0) "" else item.monto.toString().replace(".0", ""), 
                    onValueChange = { 
                        val filtered = it.filter { c -> c.isDigit() || c == '.' }
                        onUpdate(item.copy(monto = filtered.toDoubleOrNull() ?: 0.0)) 
                    }, 
                    placeholder = { Text("Monto") }, 
                    modifier = Modifier.weight(1f), 
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number), 
                    prefix = { Text("$") },
                    visualTransformation = thousandSeparator,
                    shape = RoundedCornerShape(8.dp)
                )
                Box(modifier = Modifier.weight(1f).clickable { onPickFecha() }) {
                    OutlinedTextField(
                        value = item.fechaDevengamiento.toMesAnio(),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Origen", fontSize = 10.sp) },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = false,
                        trailingIcon = { Icon(Icons.Default.CalendarToday, null, modifier = Modifier.size(16.dp)) },
                        colors = OutlinedTextFieldDefaults.colors(
                            disabledTextColor = MaterialTheme.colorScheme.onSurface,
                            disabledBorderColor = MaterialTheme.colorScheme.outline,
                            disabledTrailingIconColor = MaterialTheme.colorScheme.onSurfaceVariant
                        ),
                        shape = RoundedCornerShape(8.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun ActualizacionChip(text: String, selected: Boolean, onClick: () -> Unit) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(12.dp),
        color = if (selected) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
        border = androidx.compose.foundation.BorderStroke(1.dp, if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outline),
        modifier = Modifier.height(40.dp).width(120.dp)
    ) {
        Box(contentAlignment = Alignment.Center) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (selected) {
                    Icon(Icons.Default.Check, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(16.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                }
                Text(text, fontSize = 13.sp, fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal)
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// MODELOS DE UI — Locales
// ─────────────────────────────────────────────────────────────────────────────

data class ItemDeuda(
    val id: String = Clock.System.now().toEpochMilliseconds().toString() + (0..999).random(),
    val descripcion: String = "",
    val monto: Double = 0.0,
    val fechaDevengamiento: LocalDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
)

data class ResultadoItemDeuda(
    val item: ItemDeuda,
    val intereses: Double,
    val totalItem: Double,
    val diasTranscurridos: Long,
    val tasaEfectivaAplicada: Double,
    val periodos: List<TrasoPeriodo>
)

data class ResultadoLiquidadorDeuda(
    val items: List<ResultadoItemDeuda>,
    val capitalTotal: Double,
    val interesesTotal: Double,
    val totalGeneral: Double,
    val tipoTasa: TipoTasa,
    val capitalizacion: TipoCapitalizacion,
    val fechaCorte: LocalDate,
    val numeroExpediente: String,
    val actorDemandado: String
)
